import { createContext, useState, useContext, useEffect } from "react";
import BASE_URL from "../Utilities/BASE_URL";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const storedUser = localStorage.getItem("user");
        const storedToken = localStorage.getItem("token");

        if (storedUser && storedToken) {
          // Set initial state from localStorage
          setUser(JSON.parse(storedUser));
          setToken(storedToken);
          setIsLoggedIn(true);

          // Verify the token with the server, but don't reset isLoggedIn until we know it's invalid
          await verifySession(storedToken);
        } else {
          // No stored credentials, we're definitely not logged in
          setLoading(false);
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        setLoading(false);
      }
    })();
  }, []);

  const verifySession = async (existingToken) => {
    if (!existingToken) return;

    // Track verification attempts to prevent excessive API calls
    const verificationKey = "lastVerificationTime";
    const lastVerificationTime = localStorage.getItem(verificationKey);
    const currentTime = Date.now();

    // Only verify if it's been more than 5 seconds since last verification
    // This prevents multiple verification requests during rapid page refreshes
    if (
      lastVerificationTime &&
      currentTime - parseInt(lastVerificationTime) < 5000
    ) {
      console.log("Skipping verification - too soon since last check");
      setLoading(false);
      return;
    }

    try {
      // Record this verification attempt
      localStorage.setItem(verificationKey, currentTime.toString());

      const res = await fetch(`${BASE_URL}/auth/verify`, {
        method: "GET",
        headers: { Authorization: `Bearer ${existingToken}` },
        credentials: "include",
      });

      if (!res.ok) {
        // Only clear auth if we get an explicit auth error (401)
        if (res.status === 401) {
          throw new Error("Not authenticated");
        } else {
          // For other errors (like network/server issues), keep the user logged in
          console.warn("Non-auth error during verification:", res.status);
          // Don't throw, just log the warning and keep user logged in
        }
      } else {
        // Successful verification
        const result = await res.json();
        const userData = result?.data?.user;
        if (userData) {
          setUser(userData);
          setIsLoggedIn(true);
          localStorage.setItem("user", JSON.stringify(userData));
        }
      }
    } catch (e) {
      if (e.message === "Not authenticated") {
        // Only log out on explicit authentication failures
        console.error("Session verification failed:", e.message);
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        setUser(null);
        setToken(null);
        setIsLoggedIn(false);
      } else {
        // For network errors or other issues, keep the user logged in
        console.warn(
          "Error during verification but keeping session:",
          e.message
        );
      }
    } finally {
      // Always set loading to false after verification completes
      setLoading(false);
    }
  };

  const login = (userData, authToken) => {
    localStorage.setItem("user", JSON.stringify(userData));
    if (authToken) {
      localStorage.setItem("token", authToken);
      setToken(authToken);
    }
    setUser(userData);
    setIsLoggedIn(true);
  };

  const logout = async () => {
    try {
      const storedToken = localStorage.getItem("token");
      await fetch(`${BASE_URL}/auth/logout`, {
        method: "GET",
        headers: storedToken
          ? { Authorization: `Bearer ${storedToken}` }
          : undefined,
        credentials: "include",
      });
    } catch {}
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    setUser(null);
    setToken(null);
    setIsLoggedIn(false);
  };

  const value = { isLoggedIn, user, token, loading, login, logout };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
